import React from "react";
import { Link } from "react-router-dom";

/**
 * Reusable EmptyState for lists that have no data yet.
 *
 * Props:
 *   icon: Lucide icon component (required)
 *   title: string (required)
 *   description: string (optional)
 *   ctaLabel: string (optional)
 *   ctaHref: string (optional, react-router path)
 *   onCta: function (optional, if handler-based instead of route)
 *   secondaryHint: string (optional, small gray text below CTA)
 *   testid: string (optional)
 */
export function EmptyState({ icon: Icon, title, description, ctaLabel, ctaHref, onCta, secondaryHint, testid }) {
    const CtaButton = () => (
        <button type="button" onClick={onCta} data-testid={testid ? `${testid}-cta` : "empty-cta"}
            className="inline-flex items-center gap-2 h-11 px-5 rounded-lg bg-brand-orange hover:bg-orange-400 text-slate-950 font-semibold text-sm uppercase tracking-wider transition">
            {ctaLabel}
        </button>
    );
    return (
        <div data-testid={testid || "empty-state"}
            className="py-16 px-6 flex flex-col items-center justify-center text-center rounded-2xl border-2 border-dashed border-slate-700 bg-slate-900/30">
            {Icon && (
                <div className="w-16 h-16 rounded-2xl bg-slate-800/60 flex items-center justify-center mb-4">
                    <Icon size={32} className="text-slate-500" strokeWidth={1.5}/>
                </div>
            )}
            <h3 className="font-display text-xl font-semibold text-slate-100 mb-2">{title}</h3>
            {description && <p className="text-sm text-slate-400 max-w-md mb-6">{description}</p>}
            {ctaLabel && (ctaHref ? (
                <Link to={ctaHref} data-testid={testid ? `${testid}-cta` : "empty-cta"}
                    className="inline-flex items-center gap-2 h-11 px-5 rounded-lg bg-brand-orange hover:bg-orange-400 text-slate-950 font-semibold text-sm uppercase tracking-wider transition">
                    {ctaLabel}
                </Link>
            ) : <CtaButton/>)}
            {secondaryHint && <p className="text-xs text-slate-500 mt-4 max-w-md">{secondaryHint}</p>}
        </div>
    );
}

export default EmptyState;
